import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get('phone_alt') as string || '').trim();
    if (honeypotValue) {
      setFormStatus('success');
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt') {
          body.append(key, value as string);
        }
      });

      const response = await fetch('https://readdy.ai/api/form/d9s8vs6e3oq4jqumu790', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch (_e) { /* ignore parse errors */ }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    }
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: '庭院花园',
        url: 'https://tingyuanhuayuan.cn/',
        description: '专注庭院花园设计与草坪造景，提供从草坪选种、养护技巧到景观搭配的专业花园绿化解决方案。',
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: '400-888-9999',
          contactType: 'customer service',
          email: 'contact@tingyuanhuayuan.cn',
        },
        address: {
          '@type': 'PostalAddress',
          streetAddress: '园林路168号',
          addressLocality: '上海',
          addressRegion: '浦东新区',
          addressCountry: 'CN',
        },
      },
      {
        '@type': 'AboutPage',
        name: '关于我们 - 庭院花园',
        url: 'https://tingyuanhuayuan.cn/about',
        isPartOf: { '@type': 'WebSite', name: '庭院花园' },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://tingyuanhuayuan.cn/' },
          { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://tingyuanhuayuan.cn/about' },
        ],
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <Navbar />

      <main className="pt-16 md:pt-[72px]">
        <Breadcrumb />

        {/* About Header */}
        <section className="relative w-full h-56 md:h-72 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20beautiful%20and%20serene%20garden%20design%20studio%20setting%20with%20architectural%20drawings%20and%20plant%20samples%20on%20a%20wooden%20table%2C%20overlooking%20a%20lush%20green%20garden%20through%20large%20windows%2C%20warm%20natural%20lighting%2C%20creative%20professional%20workspace%2C%20garden%20design%20aesthetic&width=1600&height=600&seq=about-hero-2026&orientation=landscape"
            alt="关于庭院花园"
            title="关于庭院花园 - 花园设计品牌"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/50"></div>
          <div className="relative z-10 w-full h-full flex flex-col items-center justify-center px-4">
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-white mb-2">关于我们</h1>
            <p className="text-sm md:text-base text-white/75">了解庭院花园的品牌故事与专业团队</p>
          </div>
        </section>

        {/* Brand Introduction */}
        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-50">
          <div className="max-w-[900px] mx-auto">
            <div className="flex flex-col lg:flex-row gap-8 md:gap-12 items-center">
              <div className="w-full lg:w-2/5">
                <div className="w-full h-64 md:h-80 rounded-xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=A%20professional%20team%20of%20garden%20designers%20and%20landscape%20architects%20working%20together%20in%20a%20beautiful%20outdoor%20garden%20setting%2C%20discussing%20design%20plans%2C%20lush%20green%20background%2C%20creative%20collaboration%2C%20warm%20natural%20light%2C%20professional%20yet%20friendly%20atmosphere&width=800&height=1000&seq=about-brand-2026&orientation=portrait"
                    alt="庭院花园设计团队"
                    title="庭院花园设计团队 | 花园设计"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
              <div className="flex-1">
                <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-3">品牌故事</span>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                  专注庭院花园设计与草坪造景
                </h2>
                <div className="space-y-3 text-sm md:text-base text-foreground-600 leading-relaxed">
                  <p>
                    庭院花园成立于2015年，是一家专注于庭院花园设计与草坪造景的专业机构。
                    我们拥有一支由景观设计师、园艺师和草坪养护专家组成的专业团队，为全国各地的家庭和企业提供高品质的花园绿化解决方案。
                  </p>
                  <p>
                    从草坪选种到景观搭配，从花园设计到养护管理，我们始终坚持"让每一个庭院都成为绿色艺术品"的理念。
                    无论是小型阳台绿化、中型庭院设计，还是大型庄园景观规划，我们都能提供量身定制的专业方案。
                  </p>
                  <p>
                    多年来，我们已累计为超过2000个家庭和300家企业提供了庭院花园设计与草坪养护服务，
                    客户满意度达到98%以上。我们深知，一个精心打造的花园不仅能提升生活品质，更能滋养心灵。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats / Reputation */}
        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
          <div className="max-w-[1000px] mx-auto">
            <div className="text-center mb-10">
              <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-3">品牌口碑</span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">值得信赖的花园设计品牌</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {[
                { value: '2,000+', label: '服务家庭', icon: 'ri-home-heart-line' },
                { value: '300+', label: '企业客户', icon: 'ri-building-line' },
                { value: '98%', label: '客户满意度', icon: 'ri-star-line' },
                { value: '8年', label: '专业经验', icon: 'ri-award-line' },
              ].map((stat, idx) => (
                <div key={idx} className="text-center p-5 md:p-6 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                    <i className={`${stat.icon} text-xl`}></i>
                  </div>
                  <div className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">{stat.value}</div>
                  <div className="text-xs md:text-sm text-foreground-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-50">
          <div className="max-w-[1000px] mx-auto">
            <div className="text-center mb-10">
              <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium mb-3">用户口碑</span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">客户怎么说</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              {[
                {
                  name: '张女士',
                  location: '上海 · 别墅庭院',
                  text: '庭院花园的设计团队非常专业，从初期沟通到最终施工完成，每个环节都让我很放心。现在我的庭院绿草如茵，花园景观四季有景，每天回家都是一种享受。',
                  rating: 5,
                },
                {
                  name: '李先生',
                  location: '杭州 · 中式庭院',
                  text: '我家的庭院面积不大，但设计师巧妙运用了假山、水景和草坪的搭配，让小空间也有了大格局。特别是草坪养护方面的指导非常实用，让我能轻松打理。',
                  rating: 5,
                },
                {
                  name: '王总',
                  location: '北京 · 企业园区',
                  text: '公司园区景观改造选择了庭院花园团队，他们对草坪品种的选择和整体景观规划都非常专业。改造后的园区绿意盎然，员工和客户都赞不绝口。',
                  rating: 5,
                },
              ].map((review, idx) => (
                <div key={idx} className="p-5 md:p-6 rounded-xl bg-background-100 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: review.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    &ldquo;{review.text}&rdquo;
                  </p>
                  <div className="flex items-center gap-2 border-t border-background-200/70 pt-3">
                    <div className="w-8 h-8 rounded-full bg-secondary-200 flex items-center justify-center text-secondary-700 text-xs font-bold">
                      {review.name[0]}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-800">{review.name}</div>
                      <div className="text-xs text-foreground-400">{review.location}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
          <div className="max-w-[600px] mx-auto">
            <div className="text-center mb-8">
              <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-3">联系我们</span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">获取您的专属花园方案</h2>
              <p className="mt-2 text-sm text-foreground-500">填写下方表单，我们的设计师将在24小时内与您联系</p>
            </div>

            <form onSubmit={handleSubmit} data-readdy-form="" className="space-y-4 p-6 rounded-xl bg-background-50 border border-background-200/70" noValidate>
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">您的姓名 <span className="text-red-400">*</span></label>
                <input type="text" id="name" name="name" required className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors" placeholder="请输入您的姓名" />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">电子邮箱 <span className="text-red-400">*</span></label>
                <input type="email" id="email" name="email" required className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors" placeholder="请输入您的邮箱" />
              </div>
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-foreground-700 mb-1.5">联系电话</label>
                <input type="tel" id="phone" name="phone" className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors" placeholder="请输入您的电话" />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">您的需求描述</label>
                <textarea id="message" name="message" rows={4} maxLength={500} className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors resize-none" placeholder="请描述您的庭院情况与设计需求（500字以内）"></textarea>
                <p className="text-xs text-foreground-400 mt-1">最多500字符</p>
              </div>

              <div className="contact-form-honeypot">
                <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
              </div>

              {formStatus === 'success' && (
                <div className="p-3 rounded-lg bg-primary-50 text-primary-700 text-sm flex items-center gap-2">
                  <i className="ri-checkbox-circle-line"></i>
                  提交成功！我们将在24小时内与您联系。
                </div>
              )}
              {formStatus === 'error' && (
                <div className="p-3 rounded-lg bg-red-50 text-red-600 text-sm flex items-center gap-2">
                  <i className="ri-error-warning-line"></i>
                  {formError}
                </div>
              )}

              <button
                type="submit"
                disabled={formStatus === 'loading'}
                className="w-full py-3 rounded-lg bg-primary-500 text-white font-medium text-sm hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap cursor-pointer"
              >
                {formStatus === 'loading' ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i> 提交中...
                  </span>
                ) : '提交需求'}
              </button>
            </form>
          </div>
        </section>

        {/* Contact Info */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-[900px] mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
              {[
                { icon: 'ri-mail-line', title: '电子邮箱', content: 'contact@tingyuanhuayuan.cn' },
                { icon: 'ri-phone-line', title: '联系电话', content: '400-888-9999' },
                { icon: 'ri-map-pin-line', title: '公司地址', content: '上海市浦东新区园林路168号' },
              ].map((item, idx) => (
                <div key={idx} className="p-5 rounded-xl bg-background-100 border border-background-200/70 text-center">
                  <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className={`${item.icon} text-lg`}></i>
                  </div>
                  <h3 className="text-sm font-medium text-foreground-800 mb-1">{item.title}</h3>
                  <p className="text-sm text-foreground-500">{item.content}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}